// client/src/components/federation/RemoteProfilePage.js
import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Avatar, Button, CircularProgress, Alert, Paper,
  Chip, Divider, List, ListItem, ListItemText,
  Grid, Card, CardContent, CardMedia, Skeleton, Stack,
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CollectionsBookmarkIcon from '@mui/icons-material/CollectionsBookmark';
import FavoriteIcon from '@mui/icons-material/Favorite';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import axiosInstance from '../../axiosInstance';
import AppBarHeader from '../AppBarHeader';
import { jwtDecode } from 'jwt-decode';

const ACTIVITY_LABELS = {
  added:        'Added to collection',
  rated:        'Rated',
  wishlisted:   'Wishlisted',
  reviewed:     'Reviewed',
  photo_posted: 'Posted photos',
  badge_earned: 'Earned a badge',
  followed_user:'Started following someone',
  milestone:    'Reached a milestone',
  shared:       'Shared',
};

const TYPE_LABELS = {
  games:          'Games',
  consoles:       'Consoles',
  lego:           'LEGO',
  movies:         'Movies',
  music:          'Music',
  books:          'Books',
  'board-games':  'Board Games',
  'trading-cards':'Trading Cards',
  manga:          'Manga',
  comics:         'Comics',
};

// ── Collection item grid ──────────────────────────────────────────────────────

function CollectionGrid({ items, loading, hasMore, onLoadMore }) {
  return (
    <Box>
      {loading && items.length === 0 && (
        <Grid container spacing={2}>
          {[...Array(6)].map((_, i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Card>
                <Skeleton variant="rectangular" height={140} />
                <CardContent>
                  <Skeleton variant="text" width="80%" />
                  <Skeleton variant="text" width="50%" />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
      {!loading && items.length === 0 && (
        <Typography variant="body2" color="text.secondary" fontStyle="italic" sx={{ py: 2 }}>
          No items to display.
        </Typography>
      )}
      {items.length > 0 && (
        <>
          <Grid container spacing={2}>
            {items.map((item, i) => (
              <Grid item xs={12} sm={6} md={4} key={item._id || i}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  {item.imageUrl && (
                    <CardMedia
                      component="img"
                      height="140"
                      image={`/api/proxy-fed-image?url=${encodeURIComponent(item.imageUrl)}`}
                      alt={item.name}
                      sx={{ objectFit: 'cover' }}
                      onError={e => { e.target.style.display = 'none'; }}
                    />
                  )}
                  <CardContent sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom noWrap>
                      {item.name || item.series || '—'}
                    </Typography>
                    {item.platform && (
                      <Typography variant="caption" color="text.secondary" display="block">{item.platform}</Typography>
                    )}
                    {item.year && (
                      <Typography variant="caption" color="text.secondary" display="block">{item.year}</Typography>
                    )}
                    {item.rating != null && (
                      <Typography variant="caption" color="text.secondary">
                        {'★'.repeat(item.rating)}{'☆'.repeat(5 - item.rating)}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
          {hasMore && (
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Button onClick={onLoadMore} disabled={loading} variant="outlined" size="small">
                {loading ? <CircularProgress size={16} /> : 'Load More'}
              </Button>
            </Box>
          )}
        </>
      )}
    </Box>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function RemoteProfilePage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const acct = params.get('acct');
  const token = localStorage.getItem('token');
  const user = token ? jwtDecode(token) : null;

  const [actor, setActor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [following, setFollowing] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Activity feed
  const [activities, setActivities] = useState([]);
  const [activitiesLoading, setActivitiesLoading] = useState(false);

  // Collection browser state
  const [manifest, setManifest] = useState(null);
  const [manifestLoading, setManifestLoading] = useState(false);
  const [manifestFailed, setManifestFailed] = useState(false);
  // activeCol: null = overview, { type, wishlist } = browsing a type
  const [activeCol, setActiveCol] = useState(null);
  const [colItems, setColItems] = useState([]);
  const [colPage, setColPage] = useState(1);
  const [colHasMore, setColHasMore] = useState(false);
  const [colLoading, setColLoading] = useState(false);

  const handleLogout = () => { localStorage.removeItem('token'); navigate('/login'); };

  // Resolve remote actor
  useEffect(() => {
    if (!acct) { setError('No acct parameter'); setLoading(false); return; }
    axiosInstance.get(`/fed/resolve?acct=${encodeURIComponent(acct)}`)
      .then(r => {
        const data = r.data;
        if (data.isLocal) {
          if (data.localUserId && String(data.localUserId) === String(user?.id)) navigate('/profile');
          else if (data.localUserId) navigate(`/users/${data.localUserId}`);
          else navigate('/community');
          return;
        }
        setActor(data);
        setFollowing(data.isFollowing || false);
      })
      .catch(() => setError('Could not resolve remote profile'))
      .finally(() => setLoading(false));
  }, [acct, navigate, user?.id]);

  // Fetch activities
  useEffect(() => {
    if (!actor) return;
    setActivitiesLoading(true);
    axiosInstance.get(`/fed/user-activities?acct=${encodeURIComponent(acct)}`)
      .then(r => setActivities(r.data.items || []))
      .catch(() => {})
      .finally(() => setActivitiesLoading(false));
  }, [actor, acct]);

  // Fetch collection manifest
  useEffect(() => {
    if (!actor) return;
    setManifestLoading(true);
    axiosInstance.get(`/fed/remote-collection-manifest?acct=${encodeURIComponent(acct)}`)
      .then(r => setManifest(r.data))
      .catch(() => setManifestFailed(true))
      .finally(() => setManifestLoading(false));
  }, [actor, acct]);

  // Fetch collection/wishlist items
  const fetchItems = useCallback(async (type, wishlist, page, append = false) => {
    const endpoint = wishlist ? '/fed/remote-wishlist' : '/fed/remote-collection';
    setColLoading(true);
    try {
      const r = await axiosInstance.get(endpoint, { params: { acct, type, page } });
      const { items = [], hasMore = false } = r.data;
      setColItems(prev => append ? [...prev, ...items] : items);
      setColHasMore(hasMore);
      setColPage(page);
    } catch {
      // non-fatal
    } finally {
      setColLoading(false);
    }
  }, [acct]);

  const openCollection = (type, wishlist) => {
    setActiveCol({ type, wishlist });
    setColItems([]);
    setColPage(1);
    setColHasMore(false);
    fetchItems(type, wishlist, 1, false);
  };

  const handleLoadMore = () => fetchItems(activeCol.type, activeCol.wishlist, colPage + 1, true);

  const handleFollow = async () => {
    setActionLoading(true);
    setError(null);
    try {
      await axiosInstance.post('/fed/follow-remote', { acct });
      setFollowing(true);
    } catch (e) {
      const msg = e.response?.data?.error || 'Follow failed';
      if (e.response?.status === 400 && msg === 'Already following') setFollowing(true);
      else setError(msg);
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnfollow = async () => {
    if (!actor) return;
    setActionLoading(true);
    setError(null);
    try {
      await axiosInstance.delete('/fed/follow-remote', { data: { actorUrl: actor.actorUrl } });
      setFollowing(false);
    } catch (e) {
      setError(e.response?.data?.error || 'Unfollow failed');
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    try { return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }); }
    catch { return ''; }
  };

  // Build collection/wishlist entry lists from manifest
  const collectionEntries = manifest
    ? Object.entries(manifest.collections || {}).filter(([, c]) => c > 0)
    : [];
  const wishlistEntries = manifest
    ? Object.entries(manifest.wishlists || {}).filter(([, c]) => c > 0)
    : [];

  return (
    <>
      <AppBarHeader isAdmin={user?.isAdmin} userId={user?.id} onLogout={handleLogout} />
      <Box sx={{ maxWidth: 900, mx: 'auto', p: 3 }}>
        {loading && <CircularProgress />}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {actor && !actor.isLocal && (
          <>
            {/* ── Profile card ── */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', mb: 2 }}>
                <Avatar
                  src={actor.iconUrl ? `/api/proxy-fed-image?url=${encodeURIComponent(actor.iconUrl)}` : undefined}
                  sx={{ width: 80, height: 80, flexShrink: 0 }}
                >
                  {actor.username?.[0]?.toUpperCase()}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h5" fontWeight={600}>
                    {actor.displayName || actor.username}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    @{actor.username}@{actor.domain}
                  </Typography>
                  <Chip label={actor.domain} size="small" variant="outlined" />
                </Box>
              </Box>

              {(actor.followerCount != null || actor.followingCount != null) && (
                <Box sx={{ display: 'flex', gap: 3, mb: 2 }}>
                  {actor.followerCount != null && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <PeopleIcon fontSize="small" color="action" />
                      <Typography variant="body2">
                        <strong>{actor.followerCount.toLocaleString()}</strong> followers
                      </Typography>
                    </Box>
                  )}
                  {actor.followingCount != null && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <PersonAddIcon fontSize="small" color="action" />
                      <Typography variant="body2">
                        <strong>{actor.followingCount.toLocaleString()}</strong> following
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}

              {actor.summary ? (
                <Typography variant="body1" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>{actor.summary}</Typography>
              ) : (
                <Typography variant="body2" color="text.secondary" fontStyle="italic" sx={{ mb: 2 }}>No bio provided.</Typography>
              )}

              {user && (
                <Button
                  variant={following ? 'outlined' : 'contained'}
                  color={following ? 'inherit' : 'primary'}
                  onClick={following ? handleUnfollow : handleFollow}
                  disabled={actionLoading}
                  startIcon={actionLoading ? <CircularProgress size={16} color="inherit" /> : null}
                >
                  {following ? 'Unfollow' : 'Follow'}
                </Button>
              )}
            </Paper>

            {/* ── Collections card ── */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <CollectionsBookmarkIcon color="primary" />
                <Typography variant="h6">Collections</Typography>
                {manifestLoading && <CircularProgress size={16} sx={{ ml: 1 }} />}
              </Box>

              {/* Browsing a specific type — show items */}
              {activeCol ? (
                <>
                  <Button
                    size="small"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => setActiveCol(null)}
                    sx={{ mb: 2 }}
                  >
                    {activeCol.wishlist ? 'Wishlist' : 'Collections'} overview
                  </Button>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    {activeCol.wishlist ? 'Wishlist: ' : ''}{TYPE_LABELS[activeCol.type] || activeCol.type}
                  </Typography>
                  <CollectionGrid
                    items={colItems}
                    loading={colLoading}
                    hasMore={colHasMore}
                    onLoadMore={handleLoadMore}
                  />
                </>
              ) : (
                /* Overview — show type chips */
                <>
                  {manifestFailed && (
                    <Typography variant="body2" color="text.secondary" fontStyle="italic">
                      This instance doesn't support cross-instance collection browsing yet.
                    </Typography>
                  )}

                  {!manifestLoading && !manifestFailed && collectionEntries.length === 0 && wishlistEntries.length === 0 && (
                    <Typography variant="body2" color="text.secondary" fontStyle="italic">
                      No public collections available.
                    </Typography>
                  )}

                  {collectionEntries.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Collections — click to browse
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {collectionEntries.map(([type, count]) => (
                          <Chip
                            key={type}
                            label={`${TYPE_LABELS[type] || type} (${count})`}
                            icon={<CollectionsBookmarkIcon />}
                            onClick={() => openCollection(type, false)}
                            clickable
                            color="primary"
                            variant="outlined"
                            sx={{ mb: 1 }}
                          />
                        ))}
                      </Stack>
                    </Box>
                  )}

                  {wishlistEntries.length > 0 && (
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Wishlists — click to browse
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {wishlistEntries.map(([type, count]) => (
                          <Chip
                            key={type}
                            label={`${TYPE_LABELS[type] || type} (${count})`}
                            icon={<FavoriteIcon />}
                            onClick={() => openCollection(type, true)}
                            clickable
                            color="secondary"
                            variant="outlined"
                            sx={{ mb: 1 }}
                          />
                        ))}
                      </Stack>
                    </Box>
                  )}
                </>
              )}
            </Paper>

            {/* ── Recent Activity card ── */}
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>Recent Activity</Typography>
              <Divider sx={{ mb: 2 }} />
              {activitiesLoading && <CircularProgress size={24} />}
              {!activitiesLoading && activities.length === 0 && (
                <Typography variant="body2" color="text.secondary" fontStyle="italic">
                  No recent public activity available.
                </Typography>
              )}
              {!activitiesLoading && activities.length > 0 && (
                <List disablePadding>
                  {activities.map((a, i) => (
                    <React.Fragment key={a.id || i}>
                      <ListItem disablePadding sx={{ py: 1 }}>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
                              <Chip label={ACTIVITY_LABELS[a.type] || a.type} size="small" color="primary" variant="outlined" />
                              {a.metadata?.name && (
                                <Typography variant="body2" fontWeight={600}>{a.metadata.name}</Typography>
                              )}
                            </Box>
                          }
                          secondary={
                            <Box>
                              {a.content && a.content !== a.metadata?.name && (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                  {a.content.length > 200 ? a.content.slice(0, 200) + '…' : a.content}
                                </Typography>
                              )}
                              {a.published && (
                                <Typography variant="caption" color="text.disabled">{formatDate(a.published)}</Typography>
                              )}
                            </Box>
                          }
                        />
                      </ListItem>
                      {i < activities.length - 1 && <Divider component="li" />}
                    </React.Fragment>
                  ))}
                </List>
              )}
            </Paper>
          </>
        )}
      </Box>
    </>
  );
}
